/* TimeImmerseModal — "Hoppa in i scenen".
 * The user steps INTO a historical clip and can chat with 3 NPCs in-character.
 * NPCs strongly disbelieve any warning about the upcoming disaster.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { X, Loader2, Send, ArrowLeft, Theater } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function NpcCard({ npc, onPick }) {
  return (
    <button
      onClick={() => onPick(npc)}
      data-testid={`immerse-npc-${npc.id}`}
      className="text-left p-3 rounded-2xl bg-white/85 hover:bg-white border-2 border-amber-200 hover:border-violet-400 shadow transition group"
    >
      <div className="flex items-start gap-2.5">
        <div className="text-3xl shrink-0 group-hover:scale-110 transition-transform" aria-hidden>{npc.avatar}</div>
        <div className="flex-1 min-w-0">
          <p className="font-extrabold text-sm text-amber-900 leading-tight">{npc.name}</p>
          <p className="text-[10px] uppercase tracking-widest font-bold text-amber-700/70">{npc.age} år · {npc.role}</p>
          <p className="text-[11px] text-gray-700 italic mt-1 leading-snug">{npc.brief}</p>
        </div>
      </div>
      <p className="text-[10px] text-violet-700 font-bold mt-2 opacity-0 group-hover:opacity-100 transition">💬 Prata →</p>
    </button>
  );
}

function NpcChat({ clipId, npc, scene, onBack }) {
  const [messages, setMessages] = useState([]);  // {role:'user'|'npc', text}
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    const txt = input.trim();
    if (!txt || busy) return;
    const newHist = [...messages, { role: 'user', text: txt }];
    setMessages(newHist);
    setInput('');
    setBusy(true);
    try {
      const r = await axios.post(`${API}/timefeed/clip/${clipId}/immerse/chat`, {
        npc_id: npc.id,
        message: txt,
        history: messages.slice(-8),
      }, { withCredentials: true, timeout: 60000 });
      setMessages([...newHist, { role: 'npc', text: r.data.reply }]);
    } catch (e) {
      toast.error('Hen hör dig inte just nu 💔 ' + (e?.response?.data?.detail || ''));
      setMessages(newHist);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="immerse-chat-panel">
      <div className="flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-amber-200 to-rose-200 border-b border-amber-300">
        <button onClick={onBack} className="p-1.5 rounded-full hover:bg-white/60" data-testid="immerse-back-to-scene">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="text-2xl">{npc.avatar}</div>
        <div className="flex-1 min-w-0">
          <p className="font-extrabold text-amber-900 text-sm leading-tight truncate">{npc.name}</p>
          <p className="text-[10px] uppercase tracking-widest text-amber-700/70">{npc.role} · {npc.age} år</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 bg-gradient-to-b from-amber-50/40 to-rose-50/40">
        {messages.length === 0 && (
          <div className="rounded-2xl bg-white/80 border border-amber-200 px-3 py-2.5">
            <p className="text-[11px] italic text-amber-800 leading-snug">
              <span className="font-bold not-italic">{npc.avatar} {npc.name}</span> står framför dig. Säg något — fråga, varna, eller bara småprata. Hen vet inte att du kommer från framtiden.
            </p>
          </div>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            data-testid={`immerse-msg-${m.role}-${i}`}
            className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`max-w-[82%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
              m.role === 'user'
                ? 'bg-violet-600 text-white rounded-br-md'
                : 'bg-white border border-amber-200 text-gray-800 rounded-bl-md'
            }`}>
              {m.role === 'npc' && (
                <p className="text-[9px] uppercase tracking-widest font-bold opacity-60 mb-0.5">{npc.name}</p>
              )}
              <p className="leading-snug whitespace-pre-wrap"
                 dangerouslySetInnerHTML={{
                   __html: m.text.replace(/\*([^*]+)\*/g, '<em class="opacity-70">$1</em>'),
                 }}
              />
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start" data-testid="immerse-typing">
            <div className="bg-white border border-amber-200 rounded-2xl rounded-bl-md px-3 py-2 text-xs italic text-amber-700 flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" /> {npc.name} tänker…
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="px-3 py-2.5 bg-white/85 border-t border-amber-200 flex items-center gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={`Säg något till ${npc.name}…`}
          disabled={busy}
          data-testid="immerse-input"
          className="flex-1 px-3 py-2 rounded-xl border-2 border-amber-200 focus:border-violet-400 outline-none text-sm bg-white/95"
          maxLength={300}
        />
        <button
          onClick={send}
          disabled={busy || !input.trim()}
          data-testid="immerse-send"
          className="px-3 py-2 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-bold text-xs flex items-center gap-1 disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          Säg
        </button>
      </div>
      <p className="text-[9px] text-center text-amber-700/60 italic py-1 bg-white/60">
        Tips: Försök varna {npc.name} om vad som komma skall — men hen vet inget om framtiden 🎭
      </p>
    </div>
  );
}

export default function TimeImmerseModal({ clipId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.post(`${API}/timefeed/clip/${clipId}/immerse/start`, {}, {
          withCredentials: true, timeout: 60000,
        });
        if (cancelled) return;
        setData(r.data);
      } catch (e) {
        if (cancelled) return;
        setErr(e?.response?.data?.detail || 'Kunde inte starta scenen');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clipId]);

  const clip = data?.clip;
  const bg = clip?.image_base64
    ? `url("data:${clip.image_mime || 'image/png'};base64,${clip.image_base64}")`
    : 'linear-gradient(135deg,#3D1A78,#7C5FCC,#D06892)';

  return (
    <div className="fixed inset-0 z-[360] bg-black/85 backdrop-blur-md flex items-center justify-center p-2 sm:p-4" onClick={onClose} data-testid="immerse-modal">
      <div
        className="relative w-full max-w-2xl rounded-3xl overflow-hidden shadow-2xl border-2 border-amber-300/40 max-h-[94vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{ backgroundImage: bg, backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        {/* Dark overlay for legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-black/60 pointer-events-none" />

        {/* Header */}
        <header className="relative z-10 px-4 py-3 bg-gradient-to-r from-violet-900/85 to-amber-900/85 backdrop-blur flex items-center justify-between text-white border-b border-amber-300/40">
          <div className="flex items-center gap-2 min-w-0">
            <Theater className="w-4 h-4 text-amber-300 shrink-0" />
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-amber-200 font-bold">Hoppa in i scenen</p>
              <h2 className="font-black text-sm sm:text-base leading-tight truncate" data-testid="immerse-title">
                {clip?.hook || 'Tidsresa…'}
              </h2>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/15" data-testid="immerse-close">
            <X className="w-5 h-5" />
          </button>
        </header>

        {/* Body */}
        <div className="relative z-10 flex-1 overflow-hidden flex flex-col">
          {loading && (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 px-4 text-white">
              <Loader2 className="w-10 h-10 animate-spin text-amber-300" />
              <p className="text-sm italic text-amber-100">Stiger in i {clip?.year || 'historien'}…</p>
              <p className="text-[10px] text-white/60">AI väcker scenen och personerna till liv 🎭</p>
            </div>
          )}

          {err && !loading && (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6 text-center text-white">
              <p className="text-sm">💔 {err}</p>
              <button onClick={onClose} className="px-3 py-1.5 rounded-xl bg-white/20 hover:bg-white/30 text-xs font-bold">Stäng</button>
            </div>
          )}

          {!loading && !err && data && !active && (
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="immerse-scene-view">
              {/* Scene description */}
              <div className="rounded-2xl bg-black/55 backdrop-blur px-4 py-3 border border-amber-300/30 text-white">
                <p className="text-[10px] uppercase tracking-widest font-bold text-amber-300 mb-1">
                  📍 {data.location_name} · {clip?.year}
                </p>
                <p className="text-sm leading-relaxed italic" data-testid="immerse-scene-text">
                  {data.scene}
                </p>
              </div>

              {/* NPCs */}
              <div className="space-y-2">
                <p className="text-[10px] uppercase tracking-widest font-bold text-amber-300 px-1">
                  Personer runt dig · klicka för att prata
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {(data.npcs || []).map((n) => (
                    <NpcCard key={n.id} npc={n} onPick={setActive} />
                  ))}
                </div>
              </div>

              <div className="text-center pt-1">
                <p className="text-[10px] italic text-amber-100/70">
                  🎭 Du är en osynlig gäst från framtiden. Pröva att varna dem — om någon nu skulle tro dig…
                </p>
              </div>
            </div>
          )}

          {!loading && !err && data && active && (
            <div className="flex-1 overflow-hidden bg-white/10 backdrop-blur">
              <NpcChat clipId={clipId} npc={active} scene={data} onBack={() => setActive(null)} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
